import { useCallback, useEffect, useState } from "react";

/**
 * 要素の高さを ResizeObserver で追跡する汎用 hook。
 *
 * 使い方:
 * ```tsx
 * const { ref, height } = useObservedHeight();
 * return <div ref={ref}>...</div>; // height が自動更新される
 * ```
 *
 * 典型ユースケース:
 * - オーバーレイ配置した CommentField の実高さをメッセージエリアの padding-bottom に
 *   反映し、テキストエリアが伸びても最下部メッセージが隠れないようにする。
 */
export const useObservedHeight = <T extends HTMLElement = HTMLDivElement>() => {
  const [element, setElement] = useState<T | null>(null);
  const ref = useCallback((el: T | null) => {
    setElement(el);
  }, []);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    if (!element) {
      setHeight(0);
      return;
    }
    const observer = new ResizeObserver((entries) => {
      setHeight(entries[0]?.contentRect.height ?? 0);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [element]);

  return { ref, element, height };
};
